import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	clearAdvanceFilter,
	setAdvanceFilter,
	setAdvanceFilterPage,
	setAdvanceFilterTotalPage,
	setSelectedInput,
	setSelectedInputData,
	setSelectedInputValue,
} from '../../../../../store/action/advanceFilter';
import { formatFilterName, getFiltersParams, getUniqueFilterOptions } from '../../../../../helper';
import { toast } from 'react-toastify';
import {
	generateSalesInvoicesPdf,
	getInvoicesList,
	getSummary,
	updateInvoiceBulkStatus,
} from '../../../../../services/invoices';
import { getDate } from '../../../../../utils/dateUtils';
import { hasPermission } from '../../../../../utils/controller';

const useInvoice = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const user = useSelector((state) => state?.userInfo?.userInfo?.data);
	const [bulkAction, setBulkAction] = useState([]);
	const [deselectRows, setDeselectRows] = useState(null);
	const [summaryDetails, setSummaryDetails] = useState(null);
	const [selectedInvoiceListForMarkAsSent, setSelectedInvoiceListForMarkAsSent] = useState([]);
	const [isMarkAsSentModalOpen, setIsMarkAsSentModalOpen] = useState(false);
	const [headerDropDown, setHeaderDropDown] = useState('All Invoices');
	const [sortDirection, setSortDirection] = useState({ id: 'asc' });
	const suburl = localStorage.getItem('suburl');
	const dispatch = useDispatch();
	const {
		page: advanceFilterPage,
		totalPages: advanceFilterTotalPages,
		pageSize: advanceFilterPageSize,
		orderBy: advanceFilterOrderby,
		sortBy: advanceFilterSortBy,
		selectedInputs,
		isAdvanceFilterApplied,
	} = useSelector((state) => state.advanceFilter);
	const [invoiceList, setInvoice] = useState({
		data: [],
		totalPages: 0,
		currentPage: 1,
	});
	// Restore list state (page, page_size, sorting) from sessionStorage if available
	const [invoiceParams, setInvoiceParams] = useState(() => {
		const savedState = sessionStorage.getItem('invoiceParams');
		if (savedState) {
			sessionStorage.removeItem('invoiceParams');
			return JSON.parse(savedState);
		}
		return {
			page: Number(searchParams.get('page')) || 1,
			page_size: Number(searchParams.get('page_size')) || 20,
			order_by: searchParams.get('order_by') || 'created_at',
			sort_by: searchParams.get('sort_by') || 'desc',
			filters: {},
		};
	});

	const [isLoading, setLoading] = useState(false);
	const [isLoader, setLoader] = useState(false);
	const navigate = useNavigate();
	const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = useState(false);
	const handleDeselectAll = (deselectAllRowsFn) => {
		setDeselectRows(() => deselectAllRowsFn);
	};
	const handlePageChange = (page) => {
		if (isAdvanceFilterApplied) {
			dispatch(setAdvanceFilterPage({ page }));
		} else {
			setInvoiceParams((prev) => ({
				...prev,
				page,
			}));
		}
	};

	const onRowClick = (id) => {
		if (id && hasPermission('sales.invoices.view', user?.role?.role_permissions)) {
			sessionStorage.setItem('invoiceParams', JSON.stringify(invoiceParams));
			navigate(`/user/${suburl}/invoices-details/${id}`);
		}
	};
	const FilterDatalist = (dropDownData) => {
		if (dropDownData === 'All Invoices') {
			handleClearAllFilters();
			return;
		}
		setHeaderDropDown(dropDownData);

		var selectInputData = [
			{ key: 'is_approved', value: 'Approved' },
			{ key: 'is_draft', value: 'Drafts' },
			{ key: 'is_open', value: 'Open' },
			{ key: 'is_unpaid', value: 'Unpaid' },
			{ key: 'is_partially_paid', value: 'Partially Paid' },
			{ key: 'is_paid', value: 'Paid' },
			{ key: 'is_overdue', value: 'Overdue' },
			{ key: 'is_void', value: 'Void' },
			{ key: 'is_write_off', value: 'Write Off' },
			{ key: 'is_mark_as_sent', value: 'Mark As Sent' },
		];
		const hasStatus = selectedInputs.some((input) => input.key === 'status');
		const updatedSelectedInput = hasStatus
			? selectedInputs.map((input) => {
					if (input.key === 'status') {
						return {
							...input,
							value: dropDownData,
							data: selectInputData,
						};
					}
					return input;
			  })
			: [
					...selectedInputs,
					{
						key: 'status',
						label: 'Status',
						value: dropDownData,
						data: selectInputData,
					},
			  ];
		dispatch(setSelectedInput({ key: 'status', label: 'Status' }));
		dispatch(setSelectedInputValue({ key: 'status', value: dropDownData }));
		dispatch(setSelectedInputData({ key: 'status', data: selectInputData }));
		dispatch(setAdvanceFilter({ selectedInputs: updatedSelectedInput, isAdvanceFilterApplied: true }));
		const params = getFiltersParams(updatedSelectedInput);
		handleApplyFilters(params);
	};

	async function handleApplyFilters(params) {
		try {
			if (Object.keys(params).length === 0) {
				getInvoices();
				return;
			}
			if (params.status) {
				setHeaderDropDown(params.status);
			}

			const updatedParams = { ...params };

			if (updatedParams.status === 'Approved') {
				updatedParams.status = 'is_approved';
			}

			if (updatedParams.status === 'Drafts') {
				updatedParams.status = 'is_draft';
			}
			if (updatedParams.status === 'Open') {
				updatedParams.status = 'is_open';
			}
			if (updatedParams.status === 'Unpaid') {
				updatedParams.status = 'is_unpaid';
			}
			if (updatedParams.status === 'Partially Paid') {
				updatedParams.status = 'is_partially_paid';
			}
			if (updatedParams.status === 'Paid') {
				updatedParams.status = 'is_paid';
			}
			if (updatedParams.status === 'Overdue') {
				updatedParams.status = 'is_overdue';
			}
			if (updatedParams.status === 'Void') {
				updatedParams.status = 'is_void';
			}
			if (updatedParams.status === 'Write Off') {
				updatedParams.status = 'is_write_off';
			}
			if (updatedParams.status === 'Mark As Sent') {
				updatedParams.status = 'is_mark_as_sent';
			}

			var { invoices, total_pages } = await getInvoicesList({
				order_by: advanceFilterOrderby,
				sort_by: advanceFilterSortBy,
				page: advanceFilterPage,
				page_size: advanceFilterPageSize,
				filters: updatedParams,
			});

			const invoiceLists = mappedTableData(invoices);
			setBulkAction([]);
			// Set active page for filtered invoice list
			setInvoice((prev) => ({
				...prev,
				data: invoiceLists,
				totalPages: total_pages,
				currentPage: advanceFilterPage || 1,
			}));
			dispatch(setAdvanceFilterTotalPage({ totalPages: total_pages }));
			setIsAdvanceFilterOpen(false);
		} catch (error) {
			toast.error(error?.message || 'Something went wrong! Try again.');
		}
	}

	const sortDataList = (param) => {
		const newDirection = sortDirection[param.order_by] === 'desc' ? 'asc' : 'desc';
		setSortDirection({ [param.order_by]: newDirection });
		if (isAdvanceFilterApplied) {
			dispatch(setAdvanceFilter({ order_by: param.order_by, sort_by: newDirection }));
			const params = getFiltersParams(selectedInputs);
			handleApplyFilters(params);
		} else {
			setInvoiceParams((prev) => ({
				...prev,
				order_by: param.order_by,
				sort_by: newDirection,
			}));
		}
	};

	async function handleOnFocusSelectedFilterInput(key) {
		let selectInputData = [];
		if (key !== 'status') {
			const { invoices } = await getInvoicesList({ ...invoiceParams, page_size: 1000 });
			if (key === 'customer_name') {
				selectInputData = invoices
					?.filter((item) => item['customer'])
					?.map((invoice) => ({
						key: invoice.customer.name,
						value: invoice.customer.name,
					}));
			} else if (key === 'doctor_name') {
				selectInputData = invoices
					?.filter((item) => item['doctor'] !== null)
					?.map((invoice) => ({
						key: invoice.doctor.name,
						value: invoice.doctor.name,
					}));
			} else {
				selectInputData = invoices
					?.filter((item) => item[key])
					?.map((invoice) => ({
						key: invoice[key],
						value: invoice[key],
					}));
			}
		} else {
			selectInputData = [
				{ key: 'is_approved', value: 'Approved' },
				{ key: 'is_draft', value: 'Drafts' },
				{ key: 'is_open', value: 'Open' },
				{ key: 'is_unpaid', value: 'Unpaid' },
				{ key: 'is_partially_paid', value: 'Partially Paid' },
				{ key: 'is_paid', value: 'Paid' },
				{ key: 'is_overdue', value: 'Overdue' },
				{ key: 'is_void', value: 'Void' },
				{ key: 'is_write_off', value: 'Write Off' },
				{ key: 'is_mark_as_sent', value: 'Mark As Sent' },
			];
		}
		const uniqueData = getUniqueFilterOptions(selectInputData);
		dispatch(setSelectedInputData({ key, data: uniqueData }));
		return uniqueData;
	}

	function handleOpenBulkInvoiceModal() {
		const selectedList = invoiceList.data?.reduce((acc, item) => {
			if (bulkAction.includes(item.id)) {
				acc.push(item['INVOICE']);
				return acc;
			}
			return acc;
		}, []);
		setSelectedInvoiceListForMarkAsSent(selectedList);
		setIsMarkAsSentModalOpen(true);
	}

	async function handleBulkMarkAsSent() {
		try {
			await updateInvoiceBulkStatus({ ids: bulkAction, status: 'is_mark_as_sent' });
			toast.success('Mark As Sent status updated successfully.');
			setIsMarkAsSentModalOpen(false);
			getInvoices();
		} catch (error) {
			toast.error(error.message || 'Something went wrong!');
		}
	}

	const getInvoices = async (params = invoiceParams) => {
		try {
			setLoading(true);
			var { invoices, total_pages, page } = await getInvoicesList(params);
			if (invoices) {
				const invoiceList = mappedTableData(invoices);
				setBulkAction([]);
				setInvoice((prev) => ({
					...prev,
					data: invoiceList,
					totalPages: total_pages,
					currentPage: page,
				}));
			}
		} catch {
		} finally {
			setLoading(false);
		}
	};

	function mappedTableData(data) {
		return data?.map((ele) => {
			return {
				id: ele.id,
				DATE: getDate(ele.date),
				INVOICE: ele.invoice_number || 'N/A',
				STATUS: ele?.status || 'N/A',
				AMOUNT: ele?.total_amount || 'N/A',
				'CREDIT APPLIED': (ele?.applied_credit_notes_transactions || [])
					.reduce((acc, cn) => acc + Number(cn.total_applied_amount || 0), 0)
					.toFixed(2),
			};
		});
	}

	async function getSummaryDetails() {
		try {
			var { invoice } = await getSummary();
			if (invoice) {
				setSummaryDetails(invoice);
			}
		} catch {}
	}

	async function handlePrintAndDownloadPdf(
		ids = bulkAction.join(','),
		type = 'print',
		merge = false,
	) {
		try {
			setLoader(true);
			const pdf_details = await generateSalesInvoicesPdf(
				ids === '' ? bulkAction.join(',') : ids,
				(merge = bulkAction.length === 1 ? false : true),
			);
			const url = pdf_details.signed_url;

			if (type === 'print') {
				window.open(url, '_blank');
				return;
			}

			if (type === 'download') {
				const fileName = `${merge ? 'merged' : pdf_details.meta_data.invoice_number}_sales_invoices_${Date.now()}.pdf`;
				const response = await fetch(url);
				const blob = await response.blob();
				const blobUrl = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = blobUrl;
				link.download = fileName;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(blobUrl);
			}
		} catch (err) {
		} finally {
			setLoader(false);
		}
	}

	const setHeaderDropdownStatus = (filters) => {
		const statusMap = {
			is_approved: 'Approved',
			is_draft: 'Drafts',
			is_open: 'Open',
			is_unpaid: 'Unpaid',
			is_partially_paid: 'Partially Paid',
			is_paid: 'Paid',
			is_overdue: 'Overdue',
			is_void: 'Void',
			is_write_off: 'Write Off',
			is_mark_as_sent: 'Mark As Sent',
		};
		if (filters.status !== undefined) {
			setHeaderDropDown(statusMap[filters.status]);
		}
	};

	const setFilterFromUrl = () => {
		const filters = {};
		searchParams.forEach((value, key) => {
			if (key !== 'page' && key !== 'page_size' && key !== 'order_by' && key !== 'sort_by') {
				filters[key] = value;
			}
		});
		setSortDirection({
			[searchParams.get('order_by')]: searchParams.get('sort_by') || '',
		});
		setHeaderDropdownStatus(filters);
		Object.entries(filters).forEach(([key, value]) => {
			const statusValueMap = {
				is_approved: 'Approved',
				is_draft: 'Drafts',
				is_open: 'Open',
				is_unpaid: 'Unpaid',
				is_partially_paid: 'Partially Paid',
				is_paid: 'Paid',
				is_overdue: 'Overdue',
				is_void: 'Void',
				is_write_off: 'Write Off',
				is_mark_as_sent: 'Mark As Sent',
			};
			const fieldValue = key === 'status' ? statusValueMap[value] : value;
			dispatch(
				setSelectedInput({
					key: key,
					label: formatFilterName(key),
				}),
			);
			dispatch(
				setSelectedInputValue({
					key: key,
					value: fieldValue,
				}),
			);
			handleOnFocusSelectedFilterInput(key);
		});
		if (Object.keys(filters).length > 0) {
			dispatch(
				setAdvanceFilter({
					isAdvanceFilterApplied: true,
					page: Number(searchParams.get('page')) || invoiceParams.page || 1,
					pageSize: Number(searchParams.get('page_size')) || invoiceParams.page_size || 20,
					order_by: searchParams.get('order_by') || invoiceParams.order_by || 'created_at',
					sort_by: searchParams.get('sort_by') || invoiceParams.sort_by || 'desc',
				}),
			);
		}
	};

	useEffect(() => {
		setFilterFromUrl();
	}, []);

	function getParams(params) {
		const status = params.status;
		if (!status) return params;
		setHeaderDropDown(status);
		const statusMap = {
			Approved: 'is_approved',
			Drafts: 'is_draft',
			Open: 'is_open',
			Unpaid: 'is_unpaid',
			'Partially Paid': 'is_partially_paid',
			Paid: 'is_paid',
			Overdue: 'is_overdue',
			Void: 'is_void',
			'Write Off': 'is_write_off',
			'Mark As Sent': 'is_mark_as_sent',
		};

		return {
			...params,
			status: statusMap[status],
		};
	}

	const setUrlSearchParams = () => {
		const url = new URLSearchParams();
		// Normal params
		url.set('page', isAdvanceFilterApplied ? advanceFilterPage : invoiceParams.page);
		url.set('page_size', isAdvanceFilterApplied ? advanceFilterPageSize : invoiceParams.page_size);
		url.set('order_by', isAdvanceFilterApplied ? advanceFilterOrderby : invoiceParams.order_by);
		url.set('sort_by', isAdvanceFilterApplied ? advanceFilterSortBy : invoiceParams.sort_by);
		if (selectedInputs.length > 0) {
			const filters = getParams(getFiltersParams(selectedInputs));
			Object.entries(filters).forEach(([key, value]) => {
				if (value !== undefined && value !== null && value !== '') {
					url.set(key, value);
				}
			});
		} else {
			Object.entries(invoiceParams.filters).forEach(([key, value]) => {
				url.set(key, value);
			});
		}

		setSearchParams(url);
	};

	useEffect(() => {
		setUrlSearchParams();
	}, [
		invoiceParams,
		selectedInputs,
		advanceFilterPage,
		advanceFilterPageSize,
		advanceFilterOrderby,
		advanceFilterSortBy,
		isAdvanceFilterApplied,
	]);

	// Fetch invoice list when params, filter, or pagination changes
	useEffect(() => {
		const stockFilterKeys = [
			'doctor_name',
			'business_name',
			'registration_name',
			'customer_name',
			'email',
			'mobile',
			'status',
		];
		const hasStockFilter = stockFilterKeys.some((key) => searchParams.has(key));
		if (isAdvanceFilterApplied) {
			const params = getFiltersParams(selectedInputs);
			handleApplyFilters(params);
		} else {
			if (!hasStockFilter) {
				getInvoices();
			}
		}
	}, [
		isAdvanceFilterApplied,
		invoiceParams,
		advanceFilterPage,
		advanceFilterPageSize,
		advanceFilterOrderby,
		advanceFilterSortBy,
	]);

	useEffect(() => {
		getSummaryDetails();
	}, []);

	const handleClearAllFilters = () => {
		dispatch(clearAdvanceFilter());
		setHeaderDropDown('All Invoices');
		setSearchParams({});
		const cleanParams = {
			...invoiceParams,
			page: 1,
			filters: {},
		};
		setInvoiceParams(cleanParams);
		getInvoices(cleanParams);
	};

	return {
		user,
		invoiceList,
		suburl,
		handlePageChange,
		onRowClick,
		handleDeselectAll,
		FilterDatalist,
		sortDataList,
		handleOnFocusSelectedFilterInput,
		handleApplyFilters,
		setIsAdvanceFilterOpen,
		isAdvanceFilterOpen,
		isAdvanceFilterApplied,
		selectedInputs,
		setBulkAction,
		bulkAction,
		deselectRows,
		isLoading,
		isLoader,
		setHeaderDropDown,
		headerDropDown,
		dispatch,
		sortDirection,
		advanceFilterPage,
		advanceFilterPageSize,
		advanceFilterTotalPages,
		invoiceParams,
		summaryDetails,
		handlePrintAndDownloadPdf,
		selectedInvoiceListForMarkAsSent,
		setIsMarkAsSentModalOpen,
		isMarkAsSentModalOpen,
		handleBulkMarkAsSent,
		handleOpenBulkInvoiceModal,
		setInvoiceParams,
		handleClearAllFilters
	};
};
export default useInvoice;
